import {
  ArchiveIcon,
  CaretUpDownIcon,
  ClockClockwiseIcon,
  DotsThreeVerticalIcon,
} from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { useState } from 'react';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ItemStatusEnum } from '@/modules/common';
import {
  ChangeStatusDialog,
  GenericTable,
  TableItemStatus,
} from '@/modules/common/components';

import { OverlayItem } from '../types';

interface OverlaysTableProps {
  data: OverlayItem[];
  onClick: (item: OverlayItem) => void;
  toggleStatus: (item: OverlayItem) => void;
}

export function OverlaysTable({
  data,
  toggleStatus,
  onClick,
}: OverlaysTableProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeOverlay, setActiveOverlay] = useState<OverlayItem | null>(null);
  const isArchived = activeOverlay?.status === ItemStatusEnum.Archived;

  const onArchiveRestoreClick = (
    event: React.SyntheticEvent<HTMLDivElement>,
    maskRow: OverlayItem
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveOverlay(maskRow);
  };

  const toggleModal = (nextOpenState: boolean) => {
    if (!nextOpenState) {
      setActiveOverlay(null);
    }
  };

  const toggleStatusOverlay = async () => {
    if (activeOverlay?.id) {
      setIsUpdating(true);
      try {
        await toggleStatus(activeOverlay as OverlayItem);
        setActiveOverlay(null);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const columns: ColumnDef<OverlayItem>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Overlay Name
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('name')}</span>
      ),
    },
    {
      accessorKey: 'imagesPublicUrls',
      header: 'Preview',
      cell: ({ row }) => {
        return (
          <img
            src={row.original.imagePublicUrl}
            alt="Overlay preview image"
            className="w-10 h-10 rounded-lg"
          />
        );
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Status
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) => <TableItemStatus status={row.getValue('status')} />,
    },
    {
      accessorKey: 'updatedAt',
      header: ({ column }) => {
        return (
          <Button
            variant="transparent"
            className="p-0 uppercase text-sm font-bold"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Upload Date
            <CaretUpDownIcon />
          </Button>
        );
      },
      cell: ({ row }) =>
        format(new Date(row.getValue('updatedAt')), 'yyyy-MM-dd'),
    },
    {
      size: 40,
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const isArchived = row.original.status === ItemStatusEnum.Archived;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                data-testid="overlays-table-actions"
                className="h-8 w-8 p-0"
              >
                <span className="sr-only">Open menu</span>
                <DotsThreeVerticalIcon color={colors.zinc[400]} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid={isArchived ? 'restore' : 'archive'}
                className={cn(
                  !isArchived && 'focus:text-destructive text-destructive'
                )}
                onClick={(event) => onArchiveRestoreClick(event, row.original)}
              >
                {isArchived ? (
                  <ClockClockwiseIcon />
                ) : (
                  <ArchiveIcon color="hsl(var(--destructive))" />
                )}
                {isArchived ? 'Restore' : 'Archive'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <GenericTable
        data={data}
        onClick={onClick}
        columns={columns}
        searchable
        searchColumns={['name']}
        searchPlaceholder="Search by overlay name"
        emptyDescription="No overlays uploaded yet"
      />

      <ChangeStatusDialog
        onOpenChange={toggleModal}
        isArchived={isArchived}
        isUpdating={isUpdating}
        onSubmit={toggleStatusOverlay}
        isOpen={Boolean(activeOverlay?.id)}
        title={`${isArchived ? 'Restore' : 'Archive'} overlay “${activeOverlay?.name}”?`}
        restoreDescription="Are you sure you want to restore this overlay?"
        archiveDescription="Are you sure you want to archive this overlay? You can restore it later if needed."
      />
    </>
  );
}
